/**
 * Google Places Autocomplete für Adressfelder im Buchungsprozess.
 * Das Straßenfeld dient gleichzeitig als Suchfeld; bei Auswahl werden die übrigen Felder befüllt.
 */

import {
  registerPlacesDropdownInput,
  unregisterPlacesDropdownInput,
} from "@/lib/googlePlacesDropdown";

export type GooglePlacesAddressPatch = Partial<{
  street: string;
  house_number: string;
  address_line_2: string;
  postal_code: string;
  city: string;
  state_region: string;
  country_code: string;
}>;

export function hasGooglePlacesApiKey(apiKey: string | null | undefined): boolean {
  return Boolean(apiKey?.trim());
}

let mapsLoadPromise: Promise<void> | null = null;
let mapsLoadedForKey: string | null = null;

function mapsPlacesReady(): boolean {
  return Boolean(window.google?.maps?.places?.Autocomplete);
}

/**
 * Lädt die Maps-JavaScript-API mit Places-Bibliothek (einmal pro API-Key).
 */
export function loadGoogleMapsPlaces(apiKey: string, language: "de" | "en" = "de"): Promise<void> {
  const key = apiKey.trim();
  if (!key) {
    return Promise.reject(new Error("Google Places API-Key fehlt."));
  }
  if (mapsLoadedForKey === key && mapsPlacesReady()) {
    return Promise.resolve();
  }
  if (mapsLoadPromise && mapsLoadedForKey === key) {
    return mapsLoadPromise;
  }

  mapsLoadedForKey = key;
  mapsLoadPromise = new Promise<void>((resolve, reject) => {
    if (mapsPlacesReady()) {
      resolve();
      return;
    }

    const callbackName = `__campingplatzGoogleMapsInit_${Date.now()}`;
    const win = window as unknown as Record<string, unknown>;

    win[callbackName] = () => {
      delete win[callbackName];
      if (mapsPlacesReady()) {
        resolve();
      } else {
        reject(new Error("Google Maps Places konnte nicht initialisiert werden."));
      }
    };

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-campingplatz-google-maps="true"]',
    );
    if (existing) {
      existing.remove();
    }

    const script = document.createElement("script");
    script.dataset.campingplatzGoogleMaps = "true";
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&language=${encodeURIComponent(language)}&callback=${callbackName}`;
    script.onerror = () => {
      delete win[callbackName];
      mapsLoadPromise = null;
      reject(new Error("Google Maps JavaScript API konnte nicht geladen werden."));
    };
    document.head.appendChild(script);
  });

  return mapsLoadPromise;
}

function componentValue(
  components: google.maps.GeocoderAddressComponent[],
  type: string,
  name: "long_name" | "short_name" = "long_name",
): string {
  const match = components.find((c) => c.types.includes(type));
  return match?.[name]?.trim() ?? "";
}

/**
 * Parst Google-Places-Adresskomponenten in Formularfelder (DE/AT/CH-tauglich).
 */
export function parseAddressFromComponents(
  components: google.maps.GeocoderAddressComponent[],
): GooglePlacesAddressPatch {
  const route = componentValue(components, "route");
  const streetNumber = componentValue(components, "street_number");
  const subpremise = componentValue(components, "subpremise");
  const postalCode = componentValue(components, "postal_code");
  const locality =
    componentValue(components, "locality") ||
    componentValue(components, "postal_town") ||
    componentValue(components, "sublocality") ||
    componentValue(components, "sublocality_level_1");
  const stateRegion = componentValue(components, "administrative_area_level_1");
  const countryCode = componentValue(components, "country", "short_name").toUpperCase();

  const patch: GooglePlacesAddressPatch = {};

  if (route) {
    patch.street = route;
  }
  if (streetNumber) {
    patch.house_number = streetNumber;
  }
  if (subpremise) {
    patch.address_line_2 = subpremise;
  }
  if (postalCode) {
    patch.postal_code = postalCode;
  }
  if (locality) {
    patch.city = locality;
  }
  if (stateRegion) {
    patch.state_region = stateRegion;
  }
  if (countryCode) {
    patch.country_code = countryCode;
  }

  return patch;
}

export function parseAddressFromPlace(
  place: google.maps.places.PlaceResult,
): GooglePlacesAddressPatch {
  if (!place.address_components?.length) {
    return {};
  }
  return parseAddressFromComponents(place.address_components);
}

export type AttachStreetAutocompleteOptions = {
  input: HTMLInputElement;
  apiKey: string;
  language?: "de" | "en";
  onPlaceSelected: (patch: GooglePlacesAddressPatch) => void;
};

export type StreetAutocompleteHandle = {
  destroy: () => void;
};

/**
 * Bindet Places-Autocomplete an ein Straßen-Eingabefeld (Straße = Suchfeld).
 */
export async function attachStreetAutocomplete(
  options: AttachStreetAutocompleteOptions,
): Promise<StreetAutocompleteHandle> {
  const { input, apiKey, language = "de", onPlaceSelected } = options;
  await loadGoogleMapsPlaces(apiKey, language);

  const inputId = registerPlacesDropdownInput(input);

  const autocomplete = new google.maps.places.Autocomplete(input, {
    types: ["address"],
    fields: ["address_components"],
  });

  const listener = autocomplete.addListener("place_changed", () => {
    const place = autocomplete.getPlace();
    const patch = parseAddressFromPlace(place);
    if (Object.keys(patch).length > 0) {
      onPlaceSelected(patch);
    }
  });

  return {
    destroy: () => {
      listener.remove();
      google.maps.event.clearInstanceListeners(input);
      unregisterPlacesDropdownInput(inputId);
    },
  };
}
